/**
 * Google Property Discovery Service (Story 4.5, Task 4)
 *
 * Calls Google APIs to discover available properties/accounts for each
 * connected Google platform. Executed inline during OAuth callback with
 * a 10-second total timeout.
 *
 * Each per-service function uses safeFetch() with a 5-second timeout
 * and returns AdapterResult<AccountInfo[]> — never throws.
 */

import type { Platform } from "@provenleap/db";
import type { AccountInfo, AdapterResult } from "../adapter";
import { adapterOk, adapterErr } from "../adapter";
import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";
import { API_VERSIONS } from "../config";
import { GOOGLE_INTEGRATION_SCOPES } from "./scopes";

// ============================================================================
// Types
// ============================================================================

export interface DiscoveryResult {
  platform: Platform;
  result: AdapterResult<AccountInfo[]>;
}

export interface DiscoverGooglePropertiesOptions {
  accessToken: string;
  platforms: string[];
  /** Total timeout in ms for all discovery calls (default: 10_000) */
  timeoutMs?: number;
}

// ============================================================================
// Per-Service Timeout
// ============================================================================

const PER_SERVICE_TIMEOUT_MS = 5_000;

// ============================================================================
// GA4 Discovery (Task 4.2; rewritten Story 31-15 — catalog-complete)
// ============================================================================

/**
 * Analytics Admin API version — pinned LOCALLY to `v1beta` (Story 31-15 AC#1).
 *
 * Do NOT reuse `API_VERSIONS.google` here: that constant documents itself as
 * the GA4 *Data* API version, and `analyticsadmin.googleapis.com/v1/...` does
 * not exist (live probe 2026-06-10: `/v1/accountSummaries` → 404,
 * `/v1beta/accountSummaries` → 401 auth-required). The Admin API REST surface
 * ships v1beta/v1alpha only.
 */
const GA4_ADMIN_API_VERSION = "v1beta";

/**
 * Max pages for GA4 Admin API pagination (200 per page × 10 pages = 2,000
 * entries). Mirrors the GBP precedent below: exceeding the cap returns an
 * ERROR, never a partial catalog — partial lists are unsafe for the OAuth
 * security cross-checks and the grant-resource catalog (F12 / ADR-0004).
 * Also bounds Workers subrequest usage for very large agency grants.
 */
const GA4_MAX_PAGES = 10;

/** Options for {@link discoverGA4Properties} (Story 31-15 AC#5). */
export interface DiscoverGA4PropertiesOptions {
  /**
   * GRANTED OAuth scopes, API-sourced (the callback persists Google's
   * token-response `scope` string → `oauth_tokens.scopes` →
   * `credentials.scopes`). Each AccountInfo gets the intersection of these
   * with GA4's platform scope — never the requested-scopes constant without
   * grant evidence. Omitted (orchestrator path) → `scopes: []`.
   */
  grantedScopes?: string[];
  /**
   * Observability context for structured partial-discovery warn logs
   * (Wave A pattern: tenantId + integrationId make enrichment-degrade
   * events correlatable when the 31-24 cron fans out across grants).
   */
  logContext?: { tenantId?: string; integrationId?: string };
}

/** Per-property metadata enrichment supplied by `properties.list` (AC#3). */
interface GA4PropertyDetails {
  timeZone?: string;
  currencyCode?: string;
}

/**
 * Best-effort metadata enrichment: pages `properties.list` for one account
 * and returns a propertyId → {timeZone, currencyCode} map (Story 31-15 AC#3).
 *
 * NEVER throws and NEVER fails discovery — any fetch/parse/pagination-guard
 * problem returns whatever was collected so far with `complete: false`
 * (enumeration completeness is security-critical; tz/currency is
 * display-only). `properties.list` results never add or remove catalog
 * entries (AC#4) — the caller only reads entries for already-enumerated ids.
 */
async function fetchGA4PropertyDetailsForAccount(
  accessToken: string,
  accountId: string
): Promise<{ details: Map<string, GA4PropertyDetails>; complete: boolean }> {
  const details = new Map<string, GA4PropertyDetails>();
  let pageToken: string | undefined;
  let pageCount = 0;

  do {
    const params = new URLSearchParams({
      filter: `parent:accounts/${accountId}`,
      pageSize: "200",
    });
    if (pageToken) {
      params.set("pageToken", pageToken);
    }
    const url = `https://analyticsadmin.googleapis.com/${GA4_ADMIN_API_VERSION}/properties?${params.toString()}`;

    const result = await safeFetch(
      url,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: "application/json",
        },
      },
      PER_SERVICE_TIMEOUT_MS
    );

    if (!result.success) {
      // Degrade, never error — e.g. property-level-only grants can 403 here.
      return { details, complete: false };
    }

    let data: {
      properties?: Array<{
        name?: string;
        timeZone?: string;
        currencyCode?: string;
      }>;
      nextPageToken?: string;
    };
    try {
      data = (await result.data.json()) as typeof data;
    } catch {
      return { details, complete: false };
    }

    for (const prop of data.properties ?? []) {
      if (!prop.name) continue;
      const propertyId = prop.name.replace("properties/", "");
      details.set(propertyId, {
        timeZone: prop.timeZone,
        currencyCode: prop.currencyCode,
      });
    }

    const next = data.nextPageToken;
    // Repeated-cursor + max-pages guards — for ENRICHMENT these degrade
    // (stop paging, keep what we have) rather than erroring (AC#3).
    if (next && next === pageToken) {
      return { details, complete: false };
    }
    pageToken = next;
    pageCount++;
    if (pageCount >= GA4_MAX_PAGES && pageToken) {
      return { details, complete: false };
    }
  } while (pageToken);

  return { details, complete: true };
}

/**
 * Discovers GA4 properties via the Analytics Admin API (Story 31-15).
 *
 * Catalog contract (multi-tenant — `adapter.ts` "Catalog contract (Epic 31)"):
 * - Enumeration source of truth: `accountSummaries.list` ("everything the
 *   caller can access", incl. property-level-only grants), paginated to
 *   completion (`pageSize=200` + `pageToken` loop) with max-page and
 *   repeated-cursor guards that return an ERROR — never a partial catalog.
 * - Metadata enrichment (best-effort, parallel per account, AC#3/#4):
 *   `properties.list?filter=parent:accounts/{id}` supplies
 *   `timeZone`→`timezone` / `currencyCode`→`currency`; failures degrade by
 *   omitting those keys, never failing discovery, never altering enumeration.
 * - `scopes` = intersection of granted scopes with GA4's platform scope
 *   (API-sourced; AC#5). Defensive dedupe by property id (AC#7).
 * - Empty catalog (healthy token, zero properties) → `adapterOk([])` (AC#8).
 */
export async function discoverGA4Properties(
  accessToken: string,
  options?: DiscoverGA4PropertiesOptions
): Promise<AdapterResult<AccountInfo[]>> {
  const headers = {
    Authorization: `Bearer ${accessToken}`,
    Accept: "application/json",
  };

  // --- Enumeration: accountSummaries.list, paginated to completion (AC#2) ---
  type AccountSummary = {
    account?: string;
    displayName?: string;
    propertySummaries?: Array<{
      property?: string;
      displayName?: string;
    }>;
  };

  const summaries: AccountSummary[] = [];
  let pageToken: string | undefined;
  let pageCount = 0;

  do {
    const params = new URLSearchParams({ pageSize: "200" });
    if (pageToken) {
      params.set("pageToken", pageToken);
    }
    const url = `https://analyticsadmin.googleapis.com/${GA4_ADMIN_API_VERSION}/accountSummaries?${params.toString()}`;

    const result = await safeFetch(url, { headers }, PER_SERVICE_TIMEOUT_MS);

    if (!result.success) {
      // Passthrough — safeFetch already maps per-cause codes (401 → token_expired).
      return adapterErr(result.error);
    }

    let data: {
      accountSummaries?: AccountSummary[];
      nextPageToken?: string;
    };
    try {
      data = (await result.data.json()) as typeof data;
    } catch (err) {
      return adapterErr({
        code: "invalid_response",
        message: sanitizeErrorMessage(
          `Failed to parse GA4 discovery response: ${err instanceof Error ? err.message : String(err)}`
        ),
        retryable: false,
      });
    }

    summaries.push(...(data.accountSummaries ?? []));

    const next = data.nextPageToken;
    if (next && next === pageToken) {
      // Cursor-loop defense: same nextPageToken twice consecutively (AC#2).
      return adapterErr({
        code: "provider_unavailable",
        message:
          "GA4 account summaries pagination returned a repeated page cursor. Please contact support.",
        retryable: false,
      });
    }
    pageToken = next;
    pageCount++;

    if (pageCount >= GA4_MAX_PAGES && pageToken) {
      // Error, never a partial catalog (security: incomplete cross-check).
      return adapterErr({
        code: "provider_unavailable",
        message:
          "Too many GA4 account summaries for complete validation. Please contact support.",
        retryable: false,
      });
    }
  } while (pageToken);

  // --- Enrichment: properties.list per account, parallel + best-effort (AC#3) ---
  // Only enrich accounts that contributed at least one enumerated property:
  // an account with zero propertySummaries has nothing to annotate (AC#4 —
  // enrichment never ADDS entries), so calling properties.list for it would
  // waste subrequests (OAuth-callback 10s budget / Workers limits) and, if it
  // 403s, emit a spurious degrade warn on every 31-24 cron run even though no
  // catalog row was affected. (Code-review fix, Story 31-15.)
  const accountIds = [
    ...new Set(
      summaries
        .filter((s) => (s.propertySummaries ?? []).some((p) => p.property))
        .map((s) => s.account)
        .filter((a): a is string => typeof a === "string" && a.length > 0)
        .map((a) => a.replace("accounts/", ""))
    ),
  ];

  const propertyDetails = new Map<string, GA4PropertyDetails>();
  const degradedAccountIds: string[] = [];

  const enrichmentResults = await Promise.allSettled(
    accountIds.map(async (accountId) => ({
      accountId,
      ...(await fetchGA4PropertyDetailsForAccount(accessToken, accountId)),
    }))
  );

  for (let i = 0; i < enrichmentResults.length; i++) {
    const settled = enrichmentResults[i]!;
    if (settled.status === "fulfilled") {
      for (const [propertyId, detail] of settled.value.details) {
        propertyDetails.set(propertyId, detail);
      }
      if (!settled.value.complete) {
        degradedAccountIds.push(settled.value.accountId);
      }
    } else {
      // Defensive — the helper never rejects, but degrade rather than fail.
      degradedAccountIds.push(accountIds[i]!);
    }
  }

  if (degradedAccountIds.length > 0) {
    // Structured partial-discovery warn (Wave A pattern) — the catalog is
    // complete (enumeration succeeded) but tz/currency metadata is missing
    // for these accounts. Correlatable via tenantId/integrationId when the
    // 31-24 cron fans out across grants.
    console.warn(
      JSON.stringify({
        event: "ga4_discovery_enrichment_partial",
        tenantId: options?.logContext?.tenantId,
        integrationId: options?.logContext?.integrationId,
        degradedAccountIds,
        message:
          "GA4 properties.list enrichment failed or was incomplete for some accounts; timezone/currency omitted (catalog enumeration unaffected)",
      })
    );
  }

  // --- Scopes: intersection of GRANTED scopes with GA4's platform scope (AC#5) ---
  const grantedScopes = options?.grantedScopes ?? [];
  const ga4PlatformScopes =
    GOOGLE_INTEGRATION_SCOPES["google-analytics-4"]?.scopes ?? [];
  const scopes = ga4PlatformScopes.filter((s) => grantedScopes.includes(s));

  // --- Flatten + defensive dedupe by property id (AC#7, ADR-0004 §AP-4) ---
  const accounts: AccountInfo[] = [];
  const seenIds = new Set<string>();

  for (const summary of summaries) {
    const accountId = summary.account
      ? summary.account.replace("accounts/", "")
      : undefined;

    for (const prop of summary.propertySummaries ?? []) {
      if (!prop.property) continue;
      // property is "properties/123456", extract the ID
      const propertyId = prop.property.replace("properties/", "");
      if (seenIds.has(propertyId)) continue; // first occurrence wins
      seenIds.add(propertyId);

      // Enrichment is annotation-only (AC#4): we only READ details for ids
      // already enumerated by the summaries; extra properties.list entries
      // never become catalog rows, and missing entries just omit keys.
      const detail = propertyDetails.get(propertyId);

      accounts.push({
        id: propertyId,
        name: prop.displayName ?? propertyId,
        platform: "google-analytics-4" as Platform,
        metadata: {
          accountName: summary.displayName,
          propertyResource: prop.property,
          ...(accountId !== undefined ? { accountId } : {}),
          ...(detail?.timeZone !== undefined
            ? { timezone: detail.timeZone }
            : {}),
          ...(detail?.currencyCode !== undefined
            ? { currency: detail.currencyCode }
            : {}),
        },
        // Fresh array per entry — never share a mutable reference into the
        // catalog upsert (Wave A pattern, Story 31-13).
        scopes: [...scopes],
      });
    }
  }

  return adapterOk(accounts);
}

// ============================================================================
// Google Search Console Discovery (Task 4.2)
// ============================================================================

/**
 * Discovers Search Console sites the user has access to.
 */
export async function discoverSearchConsoleSites(
  accessToken: string
): Promise<AdapterResult<AccountInfo[]>> {
  const url = "https://searchconsole.googleapis.com/webmasters/v3/sites";

  const result = await safeFetch(
    url,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: "application/json",
      },
    },
    PER_SERVICE_TIMEOUT_MS
  );

  if (!result.success) {
    return adapterErr(result.error);
  }

  try {
    const data = (await result.data.json()) as {
      siteEntry?: Array<{
        siteUrl?: string;
        permissionLevel?: string;
      }>;
    };

    const accounts: AccountInfo[] = (data.siteEntry ?? [])
      .filter((site) => site.siteUrl)
      .map((site) => ({
        id: site.siteUrl!,
        name: site.siteUrl!,
        platform: "google-search-console" as Platform,
        metadata: {
          permissionLevel: site.permissionLevel,
        },
      }));

    return adapterOk(accounts);
  } catch (err) {
    return adapterErr({
      code: "invalid_response",
      message: sanitizeErrorMessage(
        `Failed to parse Search Console discovery response: ${err instanceof Error ? err.message : String(err)}`
      ),
      retryable: false,
    });
  }
}

// ============================================================================
// Google Ads Discovery (Task 4.2)
// ============================================================================

/**
 * Discovers Google Ads accounts accessible to the user.
 * Uses the listAccessibleCustomers endpoint which doesn't require a customer ID.
 *
 * Story 31-16 AC#3: every seed row carries `scopes` from the single source of
 * truth (`GOOGLE_INTEGRATION_SCOPES["google-ads"].scopes`). The OAuth-callback
 * picker path (`discoverGoogleProperties`) inherits this — harmless, same
 * field semantics (display/consent metadata ONLY, never access control).
 *
 * NOTE: this returns ONLY directly-accessible accounts (Google excludes
 * accounts reachable solely through a manager/MCC link). Manager-subtree
 * expansion to the FULL catalog lives in the adapter's `discoverAccounts`
 * (`google-ads.ts`), NOT here — the callback picker keeps its 10s budget.
 */
export async function discoverGoogleAdsAccounts(
  accessToken: string,
  developerToken: string
): Promise<AdapterResult<AccountInfo[]>> {
  const url = `https://googleads.googleapis.com/${API_VERSIONS.googleAds}/customers:listAccessibleCustomers`;

  const result = await safeFetch(
    url,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "developer-token": developerToken,
        Accept: "application/json",
      },
    },
    PER_SERVICE_TIMEOUT_MS
  );

  if (!result.success) {
    return adapterErr(result.error);
  }

  try {
    const data = (await result.data.json()) as {
      resourceNames?: string[];
    };

    const accounts: AccountInfo[] = (data.resourceNames ?? []).map(
      (resourceName) => {
        // resourceName is "customers/1234567890"
        const customerId = resourceName.replace("customers/", "");
        return {
          id: customerId,
          name: `Account ${customerId}`,
          platform: "google-ads" as Platform,
          metadata: { resourceName },
          // Story 31-16 AC#3 — single source of truth, fresh array per entry
          // (never share a mutable reference into the catalog upsert).
          scopes: [...(GOOGLE_INTEGRATION_SCOPES["google-ads"]?.scopes ?? [])],
        };
      }
    );

    return adapterOk(accounts);
  } catch (err) {
    return adapterErr({
      code: "invalid_response",
      message: sanitizeErrorMessage(
        `Failed to parse Google Ads discovery response: ${err instanceof Error ? err.message : String(err)}`
      ),
      retryable: false,
    });
  }
}

// ============================================================================
// YouTube Analytics Discovery (Task 4.2; rewritten Story 31-18 — catalog-complete)
// ============================================================================

/**
 * Max pages for channels.list pagination (50 per page × 10 pages = 500
 * channels — far beyond any real `mine=true` grant). Mirrors GBP_MAX_PAGES:
 * exceeding the cap with a pending cursor returns an ERROR, never a partial
 * catalog (31-4 contract — discovery output is the OAuth-callback attachment
 * allowlist), and bounds buggy/echoed-cursor loops for the 31-24 cron.
 */
const YT_MAX_PAGES = 10;

/**
 * Check if a YouTube 403 response is actually quota exhaustion. YouTube
 * signals quota limits as HTTP 403 (NOT 429) with `quotaExceeded` /
 * `rateLimitExceeded` / `userRateLimitExceeded` reason strings.
 *
 * Deliberately LOCAL (duplicates `youtube-analytics.ts` `isQuotaExhausted`):
 * that file imports `discoverYouTubeChannels` from this one, so importing the
 * helper back would create a `discovery ⇄ youtube-analytics` cycle and break
 * barrel side-effect ordering (Story 31-18 AC#7).
 *
 * Matching is case-insensitive — a SUPERSET of the adapter helper's behavior
 * (code-review fix, Story 31-18): `userRateLimitExceeded` (per-user rate
 * limit, capital R) would otherwise slip through as non-retryable
 * permission_denied and make the 31-24 cron flag the grant instead of
 * backing off.
 */
function isYouTubeQuotaExhausted(
  providerMessage: string | undefined
): boolean {
  if (!providerMessage) return false;
  const msg = providerMessage.toLowerCase();
  return msg.includes("quotaexceeded") || msg.includes("ratelimitexceeded");
}

/**
 * Coerce a string-serialized uint64 statistic ("15400") to a number, or
 * `undefined` when absent/empty/non-numeric — never 0-from-empty-string and
 * never NaN (code-review fix, Story 31-18: `Number("")` is 0, which would
 * render a false "0 subscribers" in the picker UI).
 */
function coerceCount(value: string | undefined): number | undefined {
  if (typeof value !== "string" || value.trim() === "") return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Discovers YouTube channels owned by the user (Story 31-18).
 *
 * Catalog contract (multi-tenant — `adapter.ts` "Catalog contract (Epic 31)"):
 * - `channels.list?part=snippet,statistics&mine=true&maxResults=50`,
 *   paginated to completion via `nextPageToken`; hitting `YT_MAX_PAGES` with
 *   a cursor still pending returns `provider_unavailable` — NEVER a silent
 *   partial catalog. A mid-pagination failure fails the whole discovery.
 * - `id` frozen as the raw `UC…` channel id (matches `ctx.accountId` consumed
 *   by `fetchMetrics` and rows upserted by the 31-3 backfill).
 * - `scopes` from the single source of truth
 *   (`GOOGLE_INTEGRATION_SCOPES["youtube-analytics"].scopes`) — fresh array
 *   per entry; display/consent metadata ONLY, never access control.
 *   `yt-analytics-monetary.readonly` is never requested, never emitted.
 * - `metadata`: `customUrl` (key preserved for 31-3 backfill compat) +
 *   `subscriberCount`/`videoCount` (Number-coerced from string-serialized
 *   uint64s; omitted when absent/non-numeric — never NaN) +
 *   `hiddenSubscriberCount` passthrough. NO `isMonetized` — the channel
 *   resource has no monetization field and YouTube Developer Policies
 *   prohibit estimating it (story stub override).
 * - Defensive dedupe by channel id (first occurrence wins; ADR-0004 §AP-4).
 * - Empty catalog (zero channels) → `adapterOk([])` — valid success; do NOT
 *   copy `validateConnection`'s `account_not_found` semantics.
 * - Quota-403 (`quotaExceeded`/`rateLimitExceeded`) → retryable
 *   `rate_limited` so the 31-24 cron backs off instead of flagging the grant.
 *
 * Shared with the OAuth-callback orchestrator via DISCOVERY_FUNCTIONS —
 * signature `(accessToken: string)` is frozen (AC#8).
 */
export async function discoverYouTubeChannels(
  accessToken: string
): Promise<AdapterResult<AccountInfo[]>> {
  const headers = {
    Authorization: `Bearer ${accessToken}`,
    Accept: "application/json",
  };

  const channelScopes =
    GOOGLE_INTEGRATION_SCOPES["youtube-analytics"]?.scopes ?? [];

  // Defensive dedupe by channel id — first occurrence wins (AC#5).
  const channelsById = new Map<string, AccountInfo>();
  let pageToken: string | undefined;
  let pageCount = 0;

  do {
    const params = new URLSearchParams({
      part: "snippet,statistics",
      mine: "true",
      maxResults: "50",
    });
    if (pageToken) {
      params.set("pageToken", pageToken);
    }
    const url = `https://youtube.googleapis.com/youtube/v3/channels?${params.toString()}`;

    const result = await safeFetch(url, { headers }, PER_SERVICE_TIMEOUT_MS);

    if (!result.success) {
      // YouTube signals quota exhaustion as 403 (NOT 429) — map to retryable
      // rate_limited so the 31-24 cron backs off instead of flagging the
      // grant (AC#7). Mirrors executeAnalyticsQuery in youtube-analytics.ts.
      if (
        result.error.providerStatus === 403 &&
        isYouTubeQuotaExhausted(result.error.providerMessage)
      ) {
        return adapterErr({
          code: "rate_limited",
          message: "Rate limit reached. Please try again later.",
          retryable: true,
          providerStatus: 403,
          providerMessage: result.error.providerMessage,
        });
      }
      // Passthrough — safeFetch already maps per-cause codes (401 →
      // token_expired, 5xx → provider_unavailable, …). A mid-pagination
      // failure fails the WHOLE discovery — no partial catalog (AC#7).
      return adapterErr(result.error);
    }

    let data: {
      items?: Array<{
        id?: string;
        snippet?: {
          title?: string;
          customUrl?: string;
        };
        statistics?: {
          subscriberCount?: string;
          hiddenSubscriberCount?: boolean;
          videoCount?: string;
        };
      }>;
      nextPageToken?: string;
    };
    try {
      data = (await result.data.json()) as typeof data;
    } catch (err) {
      return adapterErr({
        code: "invalid_response",
        message: sanitizeErrorMessage(
          `Failed to parse YouTube discovery response: ${err instanceof Error ? err.message : String(err)}`
        ),
        retryable: false,
      });
    }

    for (const item of data.items ?? []) {
      if (!item.id) continue;
      if (channelsById.has(item.id)) continue; // first occurrence wins

      // uint64s serialize as JSON strings ("15400"); subscriberCount is
      // rounded down to 3 significant figures by YouTube policy — store raw,
      // no display formatting here. Omit (never NaN, never 0-from-"") when
      // absent/empty/non-numeric: external_account_metadata renders in the
      // picker UI (AC#3).
      const subscriberCount = coerceCount(item.statistics?.subscriberCount);
      const videoCount = coerceCount(item.statistics?.videoCount);

      channelsById.set(item.id, {
        // id frozen: raw "UC…" channel id (AC#4) — changing it orphans
        // catalog rows already upserted by the 31-3 backfill.
        id: item.id,
        name: item.snippet?.title ?? item.id,
        platform: "youtube-analytics" as Platform,
        metadata: {
          // Key name preserved exactly — 31-3 backfill compat (AC#3).
          customUrl: item.snippet?.customUrl,
          ...(subscriberCount !== undefined ? { subscriberCount } : {}),
          // When true, subscriberCount is not meaningful — downstream
          // display logic owns that decision, not the adapter.
          ...(item.statistics?.hiddenSubscriberCount !== undefined
            ? { hiddenSubscriberCount: item.statistics.hiddenSubscriberCount }
            : {}),
          ...(videoCount !== undefined ? { videoCount } : {}),
          // NO isMonetized — unobtainable from channels.list and
          // policy-prohibited to estimate (Story 31-18 stub override).
        },
        // Single source of truth, fresh array per entry — never share a
        // mutable reference into the catalog upsert (AC#2).
        scopes: [...channelScopes],
      });
    }

    const next = data.nextPageToken;
    if (next && next === pageToken) {
      // Cursor-loop defense (code-review fix, Story 31-18 — mirrors the GA4
      // 31-15 guard): an echoed cursor would otherwise burn YT_MAX_PAGES
      // identical requests (dedupe masks the echo until the cap) and then
      // surface the misleading "too many channels" message. Fail fast with
      // an accurate cause instead.
      return adapterErr({
        code: "provider_unavailable",
        message:
          "YouTube channel pagination returned a repeated page cursor. Please contact support.",
        retryable: false,
      });
    }
    pageToken = next;
    pageCount++;

    if (pageCount >= YT_MAX_PAGES && pageToken) {
      // Error, never a partial catalog (security: incomplete cross-check).
      return adapterErr({
        code: "provider_unavailable",
        message:
          "Too many channels for complete validation. Please contact support.",
        retryable: false,
      });
    }
  } while (pageToken);

  return adapterOk([...channelsById.values()]);
}

// ============================================================================
// Google Business Profile Discovery (Story 4.10, Task 2; hardened Story 31-19
// — accounts pagination + scopes + dedupe, full-catalog contract)
// ============================================================================

/** Max pages for location pagination (100 per page × 10 pages = 1000 locations) */
const GBP_MAX_PAGES = 10;

/**
 * Max pages for the Step-1 accounts list (Story 31-19 AC#2). Google's
 * `accounts.list` page size default AND maximum is **20** — so 25 pages =
 * 500 accounts, far beyond any real agency grant. Mirrors the F12
 * fail-not-partial semantics of GBP_MAX_PAGES: hitting the cap with a
 * `nextPageToken` still pending returns an ERROR, never a silently-partial
 * catalog (discovery output is the authorization allowlist for account
 * attachment in the OAuth-callback security cross-checks).
 */
const GBP_ACCOUNTS_MAX_PAGES = 25;

/**
 * Format a Google PostalAddress into a single display string.
 * Handles missing fields gracefully.
 */
function formatPostalAddress(address?: {
  addressLines?: string[];
  locality?: string;
  administrativeArea?: string;
  postalCode?: string;
}): string {
  if (!address) return "";
  const parts: string[] = [];
  if (address.addressLines?.length) {
    parts.push(address.addressLines.join(", "));
  }
  if (address.locality) parts.push(address.locality);
  if (address.administrativeArea) parts.push(address.administrativeArea);
  if (address.postalCode) parts.push(address.postalCode);
  return parts.join(", ");
}

/**
 * Discovers Google Business Profile locations using a two-step flow:
 * 1. List accounts via Account Management API — paginated to completion
 *    (Story 31-19: `pageSize=20` — the documented default AND maximum — with
 *    a `pageToken` loop; `GBP_ACCOUNTS_MAX_PAGES` cap fails-not-partial)
 * 2. List locations per account via Business Information API (paginated,
 *    `GBP_MAX_PAGES` cap — unchanged)
 *
 * This ensures parent account ID is preserved for each location (required for Reviews API).
 * Uses `readMask=name,title,storefrontAddress` for locations.
 *
 * Catalog contract (multi-tenant — `adapter.ts` "Catalog contract (Epic 31)"):
 * - Full catalog or error — any step failure (accounts fetch/parse, per-account
 *   locations fetch/parse, either page cap) fails the WHOLE discovery; never
 *   throws; empty catalog (zero accounts/locations) is `adapterOk([])`.
 * - `scopes` from the single source of truth
 *   (`GOOGLE_INTEGRATION_SCOPES["google-business-profile"].scopes`) — fresh
 *   array per entry; display/consent metadata ONLY, never access control.
 * - Defensive dedupe by location numeric id — first occurrence wins
 *   (ADR-0004 §AP-4): GBP location ids are globally unique numerics, so the
 *   same id under two accounts (personal account + location group) IS the
 *   same location; the first (typically personal) account's metadata is kept.
 *
 * Returns locations as AccountInfo[] with:
 * - id: location numeric ID (from "locations/{id}")
 * - name: business title
 * - metadata.parentAccountId: parent GBP account numeric ID
 * - metadata.propertySubtitle: formatted address string
 * - scopes: ["https://www.googleapis.com/auth/business.manage"] (via import)
 */
export async function discoverBusinessProfiles(
  accessToken: string
): Promise<AdapterResult<AccountInfo[]>> {
  const headers = {
    Authorization: `Bearer ${accessToken}`,
    Accept: "application/json",
  };

  // Step 1: List GBP accounts — paginated to completion (Story 31-19 AC#1).
  // URL aligned on API_VERSIONS.mybusinessAccountManagement, matching
  // validateConnection in gbp.ts (AC#7).
  const accountsBaseUrl = `https://mybusinessaccountmanagement.googleapis.com/${API_VERSIONS.mybusinessAccountManagement}/accounts`;

  type GBPAccount = {
    name?: string;
    accountName?: string;
    type?: string;
  };

  const gbpAccounts: GBPAccount[] = [];
  // Account-level dedupe by resource name (code-review fix, Story 31-19):
  // a duplicate account across pages (page-boundary shift between requests,
  // or a cycling cursor that evades the immediate-echo guard) would trigger
  // duplicate Step-2 location fetches — wasted quota against GBP's low QPS
  // limits. Output-equivalent: location dedupe already keeps first-wins.
  const seenAccountNames = new Set<string>();
  let accountsPageToken: string | undefined;
  let accountsPageCount = 0;

  do {
    // pageSize=20 is the documented default AND maximum for accounts.list —
    // asking for more is silently clamped, so we pin the real page size.
    const params = new URLSearchParams({ pageSize: "20" });
    if (accountsPageToken) {
      params.set("pageToken", accountsPageToken);
    }

    const accountsResult = await safeFetch(
      `${accountsBaseUrl}?${params.toString()}`,
      { headers },
      PER_SERVICE_TIMEOUT_MS
    );

    if (!accountsResult.success) {
      // Passthrough — safeFetch already maps per-cause codes (401 →
      // token_expired, 403 → permission_denied, 5xx → provider_unavailable).
      return adapterErr(accountsResult.error);
    }

    let accountsData: {
      accounts?: GBPAccount[];
      nextPageToken?: string;
    };

    try {
      accountsData = (await accountsResult.data.json()) as typeof accountsData;
    } catch (err) {
      return adapterErr({
        code: "invalid_response",
        message: sanitizeErrorMessage(
          `Failed to parse Business Profile accounts response: ${err instanceof Error ? err.message : String(err)}`
        ),
        retryable: false,
      });
    }

    // Accumulate named accounts (deduped by resource name, first occurrence
    // wins); an empty page with a nextPageToken still follows the cursor
    // (accumulate nothing, keep paging).
    for (const acct of accountsData.accounts ?? []) {
      if (!acct.name || seenAccountNames.has(acct.name)) continue;
      seenAccountNames.add(acct.name);
      gbpAccounts.push(acct);
    }

    const next = accountsData.nextPageToken;
    if (next && next === accountsPageToken) {
      // Cursor-loop defense (Wave A sibling pattern — GA4 31-15 / YT 31-18
      // review fix): an echoed cursor would otherwise burn the full page cap
      // on identical requests and surface a misleading "too many accounts"
      // message. Fail fast with an accurate cause instead.
      return adapterErr({
        code: "provider_unavailable",
        message:
          "Business Profile accounts pagination returned a repeated page cursor. Please contact support.",
        retryable: false,
      });
    }
    accountsPageToken = next;
    accountsPageCount++;

    if (accountsPageCount >= GBP_ACCOUNTS_MAX_PAGES && accountsPageToken) {
      // Error, never a partial catalog (F12 — incomplete cross-check).
      return adapterErr({
        code: "provider_unavailable",
        message:
          "Too many Business Profile accounts for complete validation. Please contact support.",
        retryable: false,
      });
    }
  } while (accountsPageToken);

  if (gbpAccounts.length === 0) {
    return adapterOk([]);
  }

  // Single source of truth — never re-type the scope literal here (AC#3;
  // cf. the 6-place-drift history in the OAuth directive).
  const gbpScopes =
    GOOGLE_INTEGRATION_SCOPES["google-business-profile"]?.scopes ?? [];

  // Step 2: List locations per account via Business Information API
  const allLocations: AccountInfo[] = [];
  // Defensive dedupe by location numeric id across ALL accounts — first
  // occurrence wins, deterministic by API account order then page order
  // (AC#4, ADR-0004 §AP-4). GBP location ids are globally unique numerics:
  // the same id reachable via two accounts IS the same location, so the DB
  // UNIQUE index is the backstop, not the first line of defense.
  const seenLocationIds = new Set<string>();

  for (const account of gbpAccounts) {
    const parentAccountId = account.name!.replace("accounts/", "");
    let nextPageToken: string | undefined;
    let pageCount = 0;

    do {
      const params = new URLSearchParams({
        readMask: "name,title,storefrontAddress",
        pageSize: "100",
      });
      if (nextPageToken) {
        params.set("pageToken", nextPageToken);
      }

      const locationsUrl = `https://mybusinessbusinessinformation.googleapis.com/${API_VERSIONS.mybusinessBusinessInformation}/${account.name}/locations?${params.toString()}`;

      const locResult = await safeFetch(
        locationsUrl,
        { headers },
        PER_SERVICE_TIMEOUT_MS
      );

      if (!locResult.success) {
        // Fail entire discovery — partial lists are unsafe for security cross-checks (F12)
        return adapterErr(locResult.error);
      }

      let locData: {
        locations?: Array<{
          name?: string;
          title?: string;
          storefrontAddress?: {
            addressLines?: string[];
            locality?: string;
            administrativeArea?: string;
            postalCode?: string;
          };
        }>;
        nextPageToken?: string;
      };

      try {
        locData = (await locResult.data.json()) as typeof locData;
      } catch {
        return adapterErr({
          code: "invalid_response",
          message: `Failed to parse location listing response for ${account.name}`,
          retryable: false,
        });
      }

      for (const loc of locData.locations ?? []) {
        // Validate location name format: "locations/{numericId}"
        if (!loc.name || !/^locations\/\d+$/.test(loc.name)) {
          continue;
        }

        const locationNumericId = loc.name.replace("locations/", "");
        if (seenLocationIds.has(locationNumericId)) continue; // first occurrence wins
        seenLocationIds.add(locationNumericId);

        const formattedAddress = formatPostalAddress(loc.storefrontAddress);

        allLocations.push({
          id: locationNumericId,
          name: loc.title ?? locationNumericId,
          platform: "google-business-profile" as Platform,
          metadata: {
            parentAccountId,
            accountName: account.accountName,
            propertySubtitle: formattedAddress || undefined,
            resourceName: loc.name,
          },
          // Fresh array per entry — never share a mutable reference into the
          // catalog upsert (Wave A pattern; display/consent metadata ONLY,
          // never access control). AC#3.
          scopes: [...gbpScopes],
        });
      }

      nextPageToken = locData.nextPageToken;
      pageCount++;

      if (pageCount >= GBP_MAX_PAGES && nextPageToken) {
        // Too many locations — return error rather than partial list (security: incomplete cross-check)
        return adapterErr({
          code: "provider_unavailable",
          message:
            "Too many locations for complete validation. Please contact support.",
          retryable: false,
        });
      }
    } while (nextPageToken);
  }

  return adapterOk(allLocations);
}

// ============================================================================
// Main Discovery Orchestrator (Task 4.1, 4.5)
// ============================================================================

/** Map platform keys to their discovery functions */
const DISCOVERY_FUNCTIONS: Record<
  string,
  (accessToken: string, ...args: string[]) => Promise<AdapterResult<AccountInfo[]>>
> = {
  // Wrapped: discoverGA4Properties takes an OPTIONS object as its second
  // param (Story 31-15 AC#5); the orchestrator's `...args: string[]` shape
  // stays call-compatible by omitting it (omitted → `scopes: []`).
  "google-analytics-4": (accessToken: string) =>
    discoverGA4Properties(accessToken),
  "google-search-console": discoverSearchConsoleSites,
  "google-ads": discoverGoogleAdsAccounts,
  "youtube-analytics": discoverYouTubeChannels,
  "google-business-profile": discoverBusinessProfiles,
};

/**
 * Discovers properties/accounts for all connected Google platforms in parallel.
 *
 * Executes inline during the OAuth callback with a total timeout (default 10s).
 * If any service discovery fails or times out, the result includes the error
 * but does not block other services.
 *
 * @returns Array of DiscoveryResult — one per platform requested
 */
export async function discoverGoogleProperties(
  options: DiscoverGooglePropertiesOptions & {
    developerToken?: string;
  }
): Promise<DiscoveryResult[]> {
  const { accessToken, platforms, timeoutMs = 10_000, developerToken } = options;

  const promises = platforms.map(async (platform): Promise<DiscoveryResult> => {
    const discoverFn = DISCOVERY_FUNCTIONS[platform];
    if (!discoverFn) {
      return {
        platform: platform as Platform,
        result: adapterOk([]),
      };
    }

    try {
      // Google Ads needs the developer token as an extra arg
      const result =
        platform === "google-ads" && developerToken
          ? await discoverGoogleAdsAccounts(accessToken, developerToken)
          : await discoverFn(accessToken);

      return { platform: platform as Platform, result };
    } catch (err) {
      return {
        platform: platform as Platform,
        result: adapterErr({
          code: "network_error",
          message: sanitizeErrorMessage(
            `Discovery failed for ${platform}: ${err instanceof Error ? err.message : String(err)}`
          ),
          retryable: true,
        }),
      };
    }
  });

  // Race all promises against a total timeout
  const timeoutPromise = new Promise<"timeout">((resolve) =>
    setTimeout(() => resolve("timeout"), timeoutMs)
  );

  const results: DiscoveryResult[] = [];

  // Use Promise.allSettled with a race against the total timeout
  const raceResult = await Promise.race([
    Promise.allSettled(promises),
    timeoutPromise,
  ]);

  if (raceResult === "timeout") {
    // Return timeout errors for all platforms
    return platforms.map((platform) => ({
      platform: platform as Platform,
      result: adapterErr({
        code: "network_error",
        message: `Discovery timed out for ${platform} (total timeout ${timeoutMs}ms exceeded)`,
        retryable: true,
      }),
    }));
  }

  // Process settled results — use index to map back to the correct platform
  for (let i = 0; i < raceResult.length; i++) {
    const settled = raceResult[i]!;
    if (settled.status === "fulfilled") {
      results.push(settled.value);
    } else {
      // This shouldn't happen since individual promises catch errors,
      // but handle it defensively
      results.push({
        platform: (platforms[i] ?? "google-analytics-4") as Platform,
        result: adapterErr({
          code: "network_error",
          message: sanitizeErrorMessage(
            `Unexpected discovery error: ${settled.reason}`
          ),
          retryable: true,
        }),
      });
    }
  }

  return results;
}
